import { Form } from 'antd';
import { Formik } from 'formik';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

const defaultLabelCol = {
    xs: 24,
    sm: 8,
    md: 4,
    lg: 4,
    xl: 4
};

// A context for capturing layout information for the form
// so the ant design
export const HVFormContext = createContext({
    layout: null,
    debug: false,
    labelCol: null
});

/**
 * A context for holding the FormikBag. Exported for testing ONLY.
 */
export const FormikBagContext = createContext({
    setFormikMethods: () => {
        return;
    },
    formikMethods: null
});

export const useFormikHelper = () => {
    return useContext(FormikBagContext);
};

/**
 * A top level helper to surface the Formik "Bag" of the currently
 * mounted Formik form. The Bag is a collection of helper function directly
 * tied to the operation of the current form. For more information,
 * see https://formik.org/docs/api/withFormik#the-formikbag
 *
 * @date 4/7/21
 * @param {object} props React props, including child nodes
 */
export const FormikHelper = props => {
    const [formikMethods, setFormikMethods] = useState(null);
    const { children } = props;
    return (
        <FormikBagContext.Provider
            value={{ ...formikMethods, setFormikMethods }}
        >
            {children}
        </FormikBagContext.Provider>
    );
};

/**
 * A component with no visibly rendered elements that sets the form's methods on mount, and unsets them on unmount.
 *
 * The methods are then passed to the FormikHelper context, which should be wrapped around every Healthvana app at the high level, so all components can easily make changes to the current form.
 *
 * See FormikForm's render method for this component's use. It will likely only be used by FormikForm
 *
 * @param {function} setErrors Takes an object with keys corresponding to the current form's fields, and values set to what errors for those fields should be. See https://formik.org/docs/api/formik#seterrors-fields--field-string-string---void
 * @param {function} setValues Takes an object with keys corresponding to the current form's fields, and values set to what values for those fields should be. See https://formik.org/docs/api/formik#setvalues-fields-reactsetstateaction-field-string-any--shouldvalidate-boolean--void
 */

const FormikHelperSetter = props => {
    const { setFormikMethods, formikMethods } = useFormikHelper();
    useEffect(() => {
        if (!formikMethods) setFormikMethods(props);
        return () => {
            if (formikMethods) setFormikMethods(null);
        };
    }, [props.values]);
    return <></>;
};

/**
 *
 * FormikForm is a wrapper that inits both
 * <Formik /> and its accompanying <Form /> element.
 *
 * Formik must be instantiated manually with initialValues
 * even if they are empty, so this does that for you.
 *
 * FormControl components used inside FormikForm have
 * special actions and defaults built in.
 *
 * For more information about the props FormikForm can take,
 * see https://formik.org/docs/api/formik
 *
 * @date 4/7/21
 * @param {string} id A unique idenifier for µee rhte form
 * @param {boolean} [debug] Whether or not the component should console log out its current values and other helpful information.
 * @param {object} children Child components.
 * @param {boolean} [enableReinitialize] Whether a Form should be able to incorporate new values of the `initialValues` prop after it has been mounted.
 * @param {object} [initialErrors] Key value pairs of errors to display immediately in the form on mount
 * @param {object} [initialStatus]
 * @param {object} [initialTouched]
 * @param {object} [initialValues={}] What values should be shown in the form fields on mount. Defaults to {} automatically unlike the unwrapped Formik libarry, so you don't have to remember to do it every time.
 * @param {boolean} [isInitialValid]
 * @param {boolean} [isSubmitting] boolean that is true while form is submitting
 * @param {object} [labelCol] Label layout, like <Col> component. Set span offset value like {span: 3, offset: 12} or sm: {span: 3, offset: 12}.
 * @param {string} [layout="vertical"]
 * @param {function} [onReset]
 * @param {function} onSubmit
 * @param {boolean} [resetOnUnmount] manually reset the form onUnmount. use with modals by passing a value that tracks whether or not the modal should be open (ie. `modalIsOpen`)
 * @param {object} [style]
 * @param {boolean} validateOnBlur
 * @param {boolean} validateOnChange
 * @param {boolean} validateOnMount
 * @param {function} validationSchema A Yup schema.
 * @param {object} wrapperCol Field column layout, like <Col> component. Set span offset value like {span: 3, offset: 12} or sm: {span: 3, offset: 12}.
 */

const FormikForm = props => {
    const {
        id = 'hvui-formikform',
        debug = false,
        children,
        enableReinitialize,
        initialErrors,
        initialStatus,
        initialTouched,
        initialValues = {},
        isInitialValid,
        isSubmitting,
        layout = 'vertical',
        onReset,
        onSubmit,
        resetOnUnmount = false,
        setFieldTouched,
        setFieldValue,
        style,
        validate,
        validateOnBlur,
        validateOnChange,
        validateOnMount,
        validationSchema,
        wrapperCol
    } = props;

    let { labelCol = defaultLabelCol } = props;

    // Specify horizontal because this looks really weird with 'inline' and 'vertical' modes
    if (layout !== 'horizontal') {
        labelCol = undefined;
    }

    const innerRef = useRef();

    const propsForFormik = {
        enableReinitialize,
        isInitialValid,
        initialErrors,
        initialStatus,
        initialTouched,
        initialValues,
        innerRef,
        isSubmitting,
        onReset,
        onSubmit,
        setFieldTouched,
        setFieldValue,
        validate,
        validateOnBlur,
        validateOnChange,
        validateOnMount,
        validationSchema
    };

    useEffect(() => {
        const formRef = innerRef.current;
        if (resetOnUnmount) {
            return () => formRef.handleReset();
        }
    }, [resetOnUnmount]);

    return (
        <HVFormContext.Provider value={{ labelCol, layout, debug }}>
            <Formik {...propsForFormik}>
                {propsFromFormik => {
                    const {
                        values,
                        setErrors: setErrorsOriginal,
                        setValues,
                        handleReset,
                        handleSubmit
                    } = propsFromFormik;

                    const methods = {
                        setErrors: errorObject => {
                            // Leaving this wrapper in case we want to do formatting here in future
                            return setErrorsOriginal(errorObject);
                        },
                        setValues
                    };
                    if (debug) {
                        console.log(`====Form Values for ${id}====`);
                        console.table(values);
                    }
                    return (
                        <form
                            onReset={handleReset}
                            onSubmit={handleSubmit}
                            style={style}
                            id={id}
                            key={`${id}-form-element`}
                        >
                            {/* We want ant's internal styles on a div (below),
                        but we'll put the submit handler right on a plain
                        form element for Formik's sake (above) */}
                            <Form
                                id={`${id}-ant-wrapper`}
                                component="div"
                                colon={false}
                                layout={layout}
                                labelCol={labelCol}
                                wrapperCol={wrapperCol}
                            >
                                {typeof children === 'function'
                                    ? children(propsFromFormik)
                                    : children}
                                <FormikHelperSetter {...methods} />
                            </Form>
                        </form>
                    );
                }}
            </Formik>
        </HVFormContext.Provider>
    );
};

export default FormikForm;
